import { JSX, useCallback, useState } from "react";
import { Input } from "@shared/EDS/components/Input";
import { RadioPanel, RadioPanelItem } from "@shared/EDS/components/RadioPanel/RadioPanel";
import { useMutation } from "@tanstack/react-query";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions.tsx";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient.ts";
import { useKnopIsLoading } from "@shared/common/hooks/useKnopIsLoading.ts";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { TractieDialog } from "@tractie/components";
import { ToggleableDialog, ToggleableDialogProps } from "@shared/dialogs/ToggleableDialog";

type Phase = "PROBLEM" | "SOLUTION";

interface FormState {
  requestId: string;
  phase: Phase;
}

export function LoadSpecificInstanceDialog({
  dialogRef,
}: Readonly<ToggleableDialogProps>): JSX.Element {
  const { unsetCurrentInstanceId, setCurrentInstanceId } = useCurrentInstanceId();
  const setLoading = useKnopIsLoading((state) => state.setLoading);
  const setReady = useKnopIsLoading((state) => state.setReady);
  const { knopClient } = useKnopClient();
  const radioPanelItems: RadioPanelItem<Phase>[] = [
    { value: "PROBLEM", label: "Aanvraag" },
    { value: "SOLUTION", label: "Oplossing" },
  ];
  const defaultPhase: Phase = "SOLUTION";
  const [selectedPhase, setSelectedPhase] = useState<Phase>(defaultPhase);
  const [requestId, setRequestId] = useState<string>("");

  const openHipPlanMutation = useMutation({
    mutationFn: ({ requestId, phase }: FormState) => {
      return phase === "PROBLEM"
        ? knopClient.openPreHipPlanByTaskId({ taskId: requestId })
        : knopClient.openHipPlanByTaskId({ taskId: requestId });
    },
    onMutate: () => {
      unsetCurrentInstanceId();
      setLoading();
    },
    onError: (err, variables) => {
      console.error("Error loading HIP plan:", err);
      messageToast({
        message:
          variables.phase === "PROBLEM"
            ? `Het laden van het plan is mislukt.`
            : `Het laden van het HIP plan is mislukt.`,
        type: "error",
      });
    },
    onSettled: () => setReady(),
    onSuccess: (response, variables) => {
      setCurrentInstanceId(response.instanceId);
      messageToast({
        message: variables.phase === "PROBLEM" ? `Plan is opgehaald.` : `HIP plan is opgehaald.`,
      });
    },
  });

  const handleSubmit = useCallback(
    (dialog: TractieDialog) => {
      dialog.close();
      openHipPlanMutation.mutate({ requestId, phase: selectedPhase });
    },
    [selectedPhase, requestId, openHipPlanMutation],
  );

  const handleCancel = useCallback(
    (dialog: TractieDialog) => {
      dialog.close();
      setSelectedPhase(defaultPhase);
      setRequestId("");
    },
    [setSelectedPhase, setRequestId, defaultPhase],
  );

  return (
    <ToggleableDialog
      dialogRef={dialogRef}
      heading="Plan laden op basis van ID"
      buttons={{
        cancel: {
          label: "Annuleren",
        },
        submit: {
          label: "Inladen",
        },
      }}
      ondialog-close={({ target }) => handleCancel(target as TractieDialog)}
      ondialog-cancel={({ target }) => handleCancel(target as TractieDialog)}
      ondialog-submit={({ target }) => handleSubmit(target as TractieDialog)}
    >
      <form className="flex flex-col gap-m">
        <fieldset className="m-0 border-0 p-0">
          <legend className="pb-xs text-s leading-l font-bold text-emphasis">
            Status genereren knoopplan
          </legend>
          <RadioPanel
            items={radioPanelItems}
            value={selectedPhase}
            onChange={(value) => value && setSelectedPhase(value)}
          />
        </fieldset>

        <label className="flex flex-col gap-xs text-s leading-l font-bold text-emphasis">
          requestID of correlationID
          <Input
            data-testid="load-specific-instance-id-input"
            maxLength={36}
            onChange={(e) => setRequestId(e.target.value)}
            value={requestId}
          />
        </label>
      </form>
    </ToggleableDialog>
  );
}
